import React, {
    ForwardedRef,
    forwardRef,
    ReactNode,
    useContext,
    useState,
} from "react";
import { useField, useFormikContext } from "formik";
import { v4 } from "uuid";
import { FormSpecialContext } from "@/Components/Form/Form";
import { InvalidFieldClassName } from "@/Components/Form/FormConstants.ts";

export interface RadioCheckProps {
    name: string;
    type: string;
    label: ReactNode;
    disabled?: boolean;
    readOnly?: boolean;
    help?: ReactNode;
    mb?: string;
    /** Set when used inside a RadioGroup so that error messages appear on the group */
    inContext?: boolean;
}

// eslint-disable-next-line react/display-name
const RadioCheck = forwardRef(
    (
        {
            type,
            label,
            mb,
            readOnly: propsReadOnly,
            disabled: propsDisabled,
            inContext,
            help,
            ...props
        }: RadioCheckProps,
        ref: ForwardedRef<HTMLInputElement>,
    ) => {
        const marginBotton = mb || "mb-3";

        const [field, meta] = useField({
            type,
            ...props,
        });
        const { readOnly: contextReadOnly, disabled: contextDisabled } =
            useContext(FormSpecialContext);
        const { isSubmitting } = useFormikContext();

        const disabled = propsDisabled || contextDisabled;
        const readOnly = propsReadOnly || contextReadOnly;

        const [id] = useState(v4());

        // const isValid = props.showValid && meta.touched && !meta.error;
        const isInvalid = meta.touched && meta.error;

        let checkStyles = "";
        if (type === "checkbox") {
            checkStyles =
                "rounded-sm indeterminate:border-indigo-600 indeterminate:bg-indigo-600";
        }

        let invalidCheckStyles = "";
        if (isInvalid) {
            invalidCheckStyles = "text-red-600 focus:ring-red-500";
        }

        return (
            <div
                className={`flex items-start ${marginBotton} ${
                    isInvalid ? InvalidFieldClassName : ""
                }`}
            >
                <div className="flex h-5 items-center">
                    <input
                        ref={ref}
                        id={id}
                        {...field}
                        {...props}
                        disabled={isSubmitting || disabled || readOnly}
                        type={type}
                        className={`h-4 w-4 border-gray-300 text-indigo-600 checked:border-indigo-600 focus:ring-indigo-500 disabled:bg-gray-100 disabled:checked:border-indigo-400 disabled:checked:bg-indigo-400 ${checkStyles} ${invalidCheckStyles}`}
                    />
                </div>
                <div className="ml-3 text-left text-sm">
                    <label
                        htmlFor={id}
                        className="flex font-medium break-words text-gray-700 select-none"
                    >
                        {label}
                    </label>

                    {!inContext && isInvalid && (
                        <p className="mt-2 text-sm text-red-600">
                            {meta.error}
                        </p>
                    )}

                    {help && <p className="text-gray-500">{help}</p>}
                </div>
            </div>
        );
    },
);

RadioCheck.displayName = "RadioCheck";

export default RadioCheck;
